import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  CobaltFlagMessageService,
  CobaltFlagMessageVariant
} from '@cae/cobalt-angular/components/flag-message';
import { AnalyticPilotEventTypes } from '@core/enums/analytic/analytic-pilot-event-types.enum';
import { AnalyticPilotPrecheckinEventPathList } from '@core/enums/analytic/analytic-pilot-precheckin-event-pathlist.enum';
import { AppRoutePaths } from '@core/enums/app-route-paths.enum';
import { AnalyticPilotPrecheckinProperties } from '@core/models/analytic/analytic-pilot-precheckin-properties.model';
import { AnalyticService } from '@core/services/analytic/analytic.service';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { PrecheckinRoutePaths } from '@precheckin/constants';
import { Customer } from '@precheckin/models/customer.model';
import { VisitStatus } from '@precheckin/models/enums/visit-status.enum';
import { ExportControlPost } from '@precheckin/models/export-control/export-control-post.model';
import { EVisitDocumentManagementStatus } from '@precheckin/models/visit/visit-document-management-status.enum';
import { VisitRecordPost } from '@precheckin/models/visit/visit-record-post.model';
import { EVisitRecordType } from '@precheckin/models/visit/visit-record-type.enum';
import { VisitRequest } from '@precheckin/models/visit/visit-request.model';
import { Visit } from '@precheckin/models/visit/visit.model';
import { VisitDocument } from '@precheckin/models/visit-document/visit-document.model';
import { HelperService } from '@precheckin/services/helper.service';
import { ScreenTimeService } from '@precheckin/services/screen-time.service';
import { VisitsService } from '@precheckin/services/visits.service';
import { LoadingCircularPanelService } from '@shared/components/loading-circular-panel/loading-circular-panel.service';
import { Observable, take } from 'rxjs';
import { AppStoreKeys } from 'src/app/store/store-keys.module';

import {
  CreateVisit,
  DisplaySurvey,
  GetQRCode,
  GetVisit,
  IsNewVisit,
  IsVisitReady,
  RecordStartPreCheckin,
  ResetVisit,
  SaveVisit,
  ShowMessage,
  SubmitVisit
} from './precheckin-visit.actions';
import {
  defaultVisitState,
  IPreCheckinVisitStateModel
} from './precheckin-visit.model';
import {
  SetHasRequestError,
  SetHasTriedToSave
} from '../shared-state/precheckin-shared-state.actions';
import { IPreCheckinTrainingStateModel } from '../training/precheckin-training.model';
import {
  GetDocuments,
  InitializeDocuments,
  SetAllDocumentsPagesReady,
  SetIsDocumentReady
} from '../visit-document/precheckin-visit-document.actions';

@State<IPreCheckinVisitStateModel>({
  name: AppStoreKeys.PreCheckinVisitState,
  defaults: defaultVisitState
})
@Injectable()
export class PreCheckinVisitState {
  visit$: Observable<Visit>;
  screenTime$: Observable<number>;

  constructor(
    private _visitService: VisitsService,
    private _store: Store,
    private _loadingCircularPanelService: LoadingCircularPanelService,
    private _cobaltFlagMessageService: CobaltFlagMessageService,
    private _helperService: HelperService,
    private _router: Router,
    private _screenTimeService: ScreenTimeService,
    private _analyticService: AnalyticService
  ) {
    this.visit$ = _store.select(PreCheckinVisitState.getVisit);
  }

  @Selector()
  public static isVisitReady(state: IPreCheckinVisitStateModel): boolean {
    return state.isVisitReady;
  }

  @Selector()
  public static getVisit(state: IPreCheckinVisitStateModel): Visit {
    return state.visit;
  }

  @Selector()
  public static getQRCode(state: IPreCheckinVisitStateModel): string {
    return state.qrCode;
  }

  @Selector()
  public static getFirstTimeFillingPrecheckin(
    state: IPreCheckinVisitStateModel
  ): boolean {
    return state.displaySurvey;
  }

  @Selector()
  public static isNewVisit(state: IPreCheckinVisitStateModel): boolean {
    return state.isNewVisit;
  }

  @Action(GetVisit)
  getVisitById(
    ctx: StateContext<IPreCheckinVisitStateModel>,
    { id }: Visit
  ): void {
    ctx.patchState({
      isVisitReady: false
    });

    this._visitService
      .getById(id)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          const trainingState = this._store.selectSnapshot(
            (s) => s.preCheckinTraining
          ) as IPreCheckinTrainingStateModel;

          if (
            (response.checkinStatus !== VisitStatus.PRECHECKED_IN ||
              response.documentManagementStatus !==
                EVisitDocumentManagementStatus.APPROVED) &&
            trainingState.trainingCenterConfiguration
              .checkinProcessConfiguration.hasDocumentManagement
          ) {
            this._store.dispatch(new GetDocuments(response.id));
          } else {
            this._store.dispatch(new SetIsDocumentReady(true));
            this._store.dispatch(new SetAllDocumentsPagesReady());
          }

          ctx.patchState({
            visit: new Visit(response),
            isVisitReady: true,
            isNewVisit: false
          });
        },
        error: () => {
          ctx.patchState({
            visit: new Visit(),
            isVisitReady: true
          });
        }
      });
  }

  @Action(ShowMessage)
  showMessage(
    ctx: StateContext<IPreCheckinVisitStateModel>,
    { showMessage }: ShowMessage
  ): void {
    ctx.patchState({
      showMessage
    });
  }

  @Action(CreateVisit)
  createVisit(
    ctx: StateContext<IPreCheckinVisitStateModel>,
    { visit }: CreateVisit
  ): void {
    this._loadingCircularPanelService.toggleLoadingCircularPanelVisibility(
      true
    );

    ctx.patchState({
      isVisitReady: false
    });

    this._visitService
      .createVisit(visit)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          const visitRecordPost = new VisitRecordPost({
            visitId: response.id,
            recordType: EVisitRecordType.START_PRECHECKIN
          });

          this._store.dispatch(new RecordStartPreCheckin(visitRecordPost));

          this._store.dispatch(
            new InitializeDocuments(new Array<VisitDocument>())
          );

          ctx.patchState({
            visit: new Visit(response),
            isVisitReady: true,
            isNewVisit: true
          });

          this._loadingCircularPanelService.toggleLoadingCircularPanelVisibility(
            false
          );
        },
        error: () => {
          ctx.patchState({
            visit: new Visit(),
            isVisitReady: true
          });

          this._loadingCircularPanelService.toggleLoadingCircularPanelVisibility(
            false
          );
        }
      });
  }

  @Action(RecordStartPreCheckin)
  recordStartPreCheckin(
    ctx: StateContext<IPreCheckinVisitStateModel>,
    { visitRecordPost }: RecordStartPreCheckin
  ): void {
    ctx.patchState({
      isVisitReady: false
    });

    this._visitService
      .recordStartPreCheckin(visitRecordPost)
      .pipe(take(1))
      .subscribe({
        next: () => {
          ctx.patchState({
            isVisitReady: true
          });
        },
        error: () => {
          ctx.patchState({
            isVisitReady: true
          });
        }
      });
  }

  @Action(SubmitVisit)
  recordSubmitVisit(
    ctx: StateContext<IPreCheckinVisitStateModel>,
    { submitPrecheckinPost }: SubmitVisit
  ): void {
    ctx.patchState({
      isVisitReady: false
    });

    this._visitService
      .recordPreCheckin(submitPrecheckinPost)
      .pipe(take(1))
      .subscribe({
        next: () => {
          const visit = new Visit(ctx.getState().visit);
          visit.checkinStatus = VisitStatus.PRECHECKED_IN;

          ctx.patchState({
            visit,
            isVisitReady: true
          });

          this.trackHeapEvent(
            AnalyticPilotEventTypes.PrecheckInCustomSubmitPrecheckIn
          );

          this._store.dispatch(new DisplaySurvey(true));

          this._router.navigate([
            AppRoutePaths.PreCheckin,
            ctx.getState().visit.trainingId,
            PrecheckinRoutePaths.Summary
          ]);
        },
        error: () => {
          ctx.patchState({
            isVisitReady: true
          });

          this._cobaltFlagMessageService.showMessage({
            message: 'Error submiting pre-check-in',
            variant: CobaltFlagMessageVariant.Error
          });

          this.trackHeapEvent(AnalyticPilotEventTypes.PrecheckInCustomErrors);
        }
      });
  }

  @Action(SaveVisit)
  saveVisit(
    ctx: StateContext<IPreCheckinVisitStateModel>,
    { visit }: SaveVisit
  ): void {
    this._loadingCircularPanelService.toggleLoadingCircularPanelVisibility(
      true
    );

    this._store.dispatch(new SetHasTriedToSave(false));

    const visitState = ctx.getState().visit;
    if (!this._helperService.areObjectsEqual(visit, visitState)) {
      this._visitService
        .saveVisit(this._createVisitRequest(visit))
        .pipe(take(1))
        .subscribe({
          next: (response) => {
            if (ctx.getState().showMessage) {
              this._cobaltFlagMessageService.showMessage({
                message: 'Pre-check-in progress saved',
                variant: CobaltFlagMessageVariant.Success
              });
            }

            ctx.patchState({
              visit: new Visit(response),
              showMessage: false,
              isNewVisit: false
            });

            this._store.dispatch(new SetHasRequestError(false));
            this._store.dispatch(new SetHasTriedToSave(true));

            this._loadingCircularPanelService.toggleLoadingCircularPanelVisibility(
              false
            );
          },
          error: () => {
            ctx.patchState({
              visit: new Visit(visit)
            });

            this._loadingCircularPanelService.toggleLoadingCircularPanelVisibility(
              false
            );

            this._cobaltFlagMessageService.showMessage({
              message: 'There was an error trying to save this precheckin.',
              variant: CobaltFlagMessageVariant.Error
            });

            this._store.dispatch(new SetHasRequestError(true));
            this._store.dispatch(new SetHasTriedToSave(true));
          }
        });
    } else {
      this._loadingCircularPanelService.toggleLoadingCircularPanelVisibility(
        false
      );

      this._store.dispatch(new SetHasTriedToSave(true));
    }
  }

  @Action(ResetVisit)
  resetVisitState(ctx: StateContext<IPreCheckinVisitStateModel>): void {
    ctx.patchState({
      visit: new Visit(),
      qrCode: '',
      isVisitReady: false,
      isNewVisit: false
    });
  }

  @Action(IsVisitReady)
  setIsVisitReady(
    ctx: StateContext<IPreCheckinVisitStateModel>,
    action: IsVisitReady
  ): void {
    ctx.patchState({
      isVisitReady: action.payload
    });
  }

  @Action(GetQRCode)
  getQRCode(
    ctx: StateContext<IPreCheckinVisitStateModel>,
    { visitId }: GetQRCode
  ): void {
    ctx.patchState({
      isVisitReady: false
    });

    this._visitService
      .getVisitQrCode(visitId)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          ctx.patchState({
            qrCode: response,
            isVisitReady: true
          });
        },
        error: () => {
          ctx.patchState({
            qrCode: 'error',
            isVisitReady: true
          });
        }
      });
  }

  private _createVisitRequest(visit: Visit): VisitRequest {
    const visitPut = new VisitRequest();
    visitPut.id = visit.id;
    visitPut.userId = visit.userId;
    visitPut.startDate = visit.startDate;
    visitPut.endDate = visit.endDate;
    visitPut.trainingId = visit.trainingId;
    visitPut.trainingCenterId = visit.trainingCenterId;
    visitPut.firstName = visit.firstName;
    visitPut.lastName = visit.lastName;
    visitPut.middleName = visit.middleName;
    visitPut.dateOfBirth = visit.dateOfBirth;
    visitPut.hotelName = visit?.hotelName;
    visitPut.hotelPhone = visit?.hotelPhone;
    visitPut.phone = visit?.phone.replace(/ /g, '.');
    visitPut.email = visit?.email;
    visitPut.emailConsentDate = visit?.emailConsentDate;
    visitPut.contactName = visit.contactName;
    visitPut.identityPolicyConsentDate = visit.identityPolicyConsentDate;
    visitPut.badgeNumber = visit?.badgeNumber;
    visitPut.visitReason = visit?.visitReason;
    visitPut.hostName = visit?.hostName;
    visitPut.lastStatusChange = visit?.lastStatusChange;
    visitPut.isBadgeActivated = visit?.isBadgeActivated;
    visitPut.exportControl = new ExportControlPost({
      countryOfResidenceId: visit.exportControl?.countryOfResidence?.id,
      lastGrantedCitizenshipId: visit.exportControl?.lastGrantedCitizenship?.id
    });
    visitPut.customer = new Customer(visit?.customer);
    visitPut.checkinStatus = visit?.checkinStatus;

    return visitPut;
  }

  trackHeapEvent(step: AnalyticPilotEventTypes): void {
    let screenTime: number;

    this._screenTimeService.getStepScreenTime().subscribe((value) => {
      screenTime = value;
    });

    const trainingState = this._store.selectSnapshot(
      (s) => s.preCheckinTraining
    ) as IPreCheckinTrainingStateModel;

    const vistState = this._store.selectSnapshot(
      (s) => s.preCheckinVisit
    ) as IPreCheckinVisitStateModel;

    this._analyticService.post(
      step,
      new AnalyticPilotPrecheckinProperties(
        vistState.visit.id,
        vistState.visit.trainingId,
        screenTime,
        trainingState.trainingCenter.geminiSiteId,
        trainingState.trainingCenter.name,
        AnalyticPilotPrecheckinEventPathList.Review,
        null,
        vistState.visit?.hotelName != null &&
          vistState.visit?.hotelPhone != null,
        null,
        null,
        null
      )
    );

    this._screenTimeService.resetScreenTime();
  }

  @Action(DisplaySurvey)
  setDisplaySurvey(
    ctx: StateContext<IPreCheckinVisitStateModel>,
    { displaySurvey }: DisplaySurvey
  ): void {
    ctx.patchState({
      displaySurvey: displaySurvey
    });
  }

  @Action(IsNewVisit)
  setIsNewVisit(
    ctx: StateContext<IPreCheckinVisitStateModel>,
    action: IsNewVisit
  ): void {
    ctx.patchState({
      isNewVisit: action.payload
    });
  }
}
